import { LIB_SCHEME, parseLibUri } from '../assets/core/uri';
import { resolveEntityDef, type PrefabTable, type WorldPrefab } from '../world/prefabs';
import { ENTITY_REGISTRY } from './registry';
import type { EntityCtx, EntityInstance, EntityModule, PlayerSnapshot } from './types';

/**
 * ── ⭐ P1.5·C · THE LIBRARY'S HALF OF A PLACEMENT (plan §13.6) ────────────────────────────────────
 *
 * A world placement whose `type` is `lib:<id>` names an `entity` ASSET instead of a registry class
 * — *"a library-level prefab, ADR-0002 tier (b) lifted out of a single world"*. The host resolves
 * it through THIS seam, which mount wires from the AssetService's catalog, and then hands the
 * result to `resolveEntityDef` exactly as it does a world prefab.
 *
 * ⭐ THAT IS THE WHOLE POINT: A LIBRARY ENTITY *IS* A PREFAB, so there is no second merge rule.
 * ADR-0002's precedence (*placement fields > prefab params > schema defaults*) is stated once, in
 * `world/prefabs.ts`, and a `lib:` placement gets it by being pushed into the table the resolver
 * already folds — chains, the `type`-smuggling guard and the depth cap included. Writing the merge
 * here would have been the second copy of the one rule this repo pins two implementations of by
 * test (`placement.scaleTriple`, `foldMaterialChain`) precisely because copies drift.
 *
 * ⚠️ IT RESOLVES AND DOES NOT ACQUIRE. A handle is a LIFETIME and the host owns none: mount holds
 * one per distinct `lib:` id for as long as the world is loaded (`librarySpawn.retain`), and the
 * CLASS acquires whatever its own params name through `EntityCtx.assets`. A host that acquired
 * would be a second ledger keyed by placement index.
 */
export interface HostLibrary {
  /** `<id>` → the prefab that id names in the catalog, or null with the reason already warned. */
  prefab(id: string): WorldPrefab | null;
}

/**
 * Entity host: turns world-data placements into live instances through the registry — resolve
 * prefab types to their base class (ADR-0002, world-data extensions), validate with the
 * registered schema, dynamic-import each required module ONCE (dedupe), create, drive, dispose.
 * Eager-safe: imports only the registry table + the pure prefab resolver, never an entity module.
 * Bad data warns and skips (constitution rule 6: unimplemented verbs warn once, never throw — a
 * world must not brick the runtime).
 */
export class EntityHost {
  private readonly modules = new Map<string, Promise<EntityModule | null>>();
  private readonly instances: EntityInstance[] = [];
  private readonly warned = new Set<string>();
  /** Current editor-mode visibility — applied to instances spawned WHILE the editor is open. */
  private editorVisible = false;
  /** True from the end of a loadAll until the next populate cycle begins (loadAll/dispose). */
  private allSpawned = false;
  /** Callbacks waiting for the current populate cycle to finish. Each fires exactly once. */
  private spawnCbs: Array<() => void> = [];
  /**
   * L3 · THE `placing` RUNG'S NUMERATOR — placements PROCESSED in the current populate cycle.
   *
   * It counts placements, not instances: a def the registry rejected has been dealt with and must
   * still move the counter, or a world with one bad row shows a bar that stops one short of its
   * denominator forever. `entities.length` is the denominator and it is known up front, which is
   * exactly what makes this the one honest counter in the first half of the boot.
   */
  private placedN = 0;
  /** Live subscribers to that counter. Fired per placement, never batched — see `onPlaced`. */
  private placedCbs: Array<(placed: number, total: number) => void> = [];

  constructor(
    private readonly ctx: EntityCtx,
    /** The `lib:` arm (P1.5·C). Absent ⇒ a `lib:` placement warns and skips, as it did before. */
    private readonly library: HostLibrary | null = null,
  ) {}

  /**
   * Resolve (through the world's prefab table) + validate + lazy-load + instantiate one
   * placement. Resolves null on invalid data (warned).
   */
  async spawn(def: unknown, prefabs: PrefabTable = {}): Promise<EntityInstance | null> {
    const placedType =
      typeof def === 'object' && def !== null && 'type' in def ? String(def.type) : '<no type>';
    /*
     * ⭐ THE `lib:` ARM. A library entity asset becomes ONE MORE ROW in the prefab table the
     * resolver below already folds, so the merge, the chain walk, the depth cap and the guard
     * against a prefab smuggling its own `type` all apply unchanged. `prefabs` itself is never
     * mutated: a world's table is data the editor edits and the emitter writes, and a synthesised
     * row must not reach either.
     */
    let table = prefabs;
    if (placedType.startsWith(LIB_SCHEME)) {
      const libId = parseLibUri(placedType);
      const prefab = libId === null ? null : (this.library?.prefab(libId) ?? null);
      if (prefab === null) {
        this.warnOnce(
          `library placement '${placedType}' did not resolve — skipped` +
            (this.library === null ? ' (no library is wired into this host)' : ''),
        );
        return null;
      }
      table = { ...prefabs, [placedType]: prefab };
    }
    const resolved = resolveEntityDef(def, table);
    if (!resolved) {
      this.warnOnce(`unresolvable entity def '${placedType}' — skipped`);
      return null;
    }
    const { baseType, def: mergedDef } = resolved;
    const via = baseType === placedType ? '' : ` (via prefab '${placedType}')`;
    const entry = ENTITY_REGISTRY[baseType];
    if (!entry) {
      this.warnOnce(`unknown entity type '${baseType}'${via} — skipped`);
      return null;
    }
    const parsed = entry.schema.safeParse(mergedDef);
    if (!parsed.success) {
      this.warnOnce(`invalid '${baseType}' def${via} — skipped: ${parsed.error.message}`);
      return null;
    }
    let mod = this.modules.get(baseType);
    if (!mod) {
      // One import per REQUIRED class, no matter how many placements use it.
      mod = entry.load().catch((err: unknown) => {
        this.warnOnce(`failed to load entity chunk '${baseType}': ${String(err)}`);
        return null;
      });
      this.modules.set(baseType, mod);
    }
    const m = await mod;
    if (!m) return null;
    /*
     * ── ⭐ B3 · THE CLASS'S SHADOW COLUMN, APPLIED BY THE HOST, NOT BY THE CLASS ────────────────
     *
     * `create()` parents whatever it draws to `ctx.root` itself (there is no per-entity group —
     * the anchor's crate is a SIBLING of its ghost on purpose), so the host attributes by DIFF: the
     * direct children of `root` that appeared during `create()` belong to this placement and get
     * `meta.shadow`. The applier stamps and hooks the subtree, which is what reaches a GLB or a
     * rig that lands under those objects seconds later. An object a class parents to `root` from
     * a later callback (pot shards, the anchor's crate at world start) is not attributable here
     * and takes the root's own default — `scene.ts` stamps `entityRoot` with
     * `ENTITY_SHADOW_DEFAULT` for exactly that.
     */
    const before = new Set(this.ctx.root.children);
    const instance = m.create(parsed.data, this.ctx);
    // The SAME diff answers two columns now: what this class does with shadows, and whether it
    // ever moves. Both are facts about the class, both are applied by the host, and neither is
    // something a `create()` should be writing for itself.
    for (const child of this.ctx.root.children) {
      if (before.has(child)) continue;
      this.ctx.applyShadowFlags?.(child, entry.meta.shadow);
      this.ctx.applyMobility?.(child, entry.meta.mobility);
    }
    this.instances.push(instance);
    // Late spawn while the editor is open (palette placement, respawn-on-edit): match the mode.
    if (this.editorVisible) instance.setEditorVisible?.(true);
    return instance;
  }

  /** Load a whole placement list (world.json `entities`, resolved through its `prefabs`). */
  async loadAll(defs: unknown[], prefabs: PrefabTable = {}): Promise<EntityInstance[]> {
    return (await this.loadAllIndexed(defs, prefabs)).filter((i): i is EntityInstance => i !== null);
  }

  /**
   * S5: `loadAll`, keeping the PLACEMENT INDEX — one slot per def, null where the def was skipped.
   * The collision binder keys physics bodies by placement index (the editor's `entityInsts` and
   * the outliner already do), and `loadAll`'s compacted list loses that the moment one def warns
   * and skips. Same populate cycle, same callbacks; `loadAll` is this with the nulls dropped.
   */
  async loadAllIndexed(defs: unknown[], prefabs: PrefabTable = {}): Promise<(EntityInstance | null)[]> {
    // A loadAll begins a new populate cycle: callbacks registered from here on (e.g. by entities
    // during their own create()) wait for THIS cycle to finish.
    this.allSpawned = false;
    // A new populate cycle re-zeroes the counter: the editor's Play transition disposes and
    // repopulates, and a numerator that kept climbing across cycles would read 12/6 on the second
    // load — a bar that is not just useless but visibly wrong.
    this.placedN = 0;
    this.emitPlaced(defs.length);
    const out: (EntityInstance | null)[] = [];
    for (const def of defs) {
      const inst = await this.spawn(def, prefabs);
      out.push(inst ?? null);
      this.placedN++;
      this.emitPlaced(defs.length);
    }
    this.allSpawned = true;
    this.flushSpawnCbs();
    return out;
  }

  /**
   * TWO-PHASE INIT hook (X0 seam 3 — the UE BeginPlay-ordering lesson: spawn order is undefined,
   * so cross-entity services must never start on any single entity's create()). Entities REGISTER
   * services during create(); services START only when the world is fully populated — i.e. when
   * loadAll has spawned every placement. `cb` fires exactly once:
   * - registered while a loadAll is pending (or before one ran) → when that loadAll completes;
   * - registered after loadAll completed → immediately (synchronously);
   * - a later loadAll/replace cycle (editor exit rebuilds: dispose + loadAll) fires only the
   *   callbacks newly registered for that cycle — already-fired callbacks never re-fire.
   * Single spawn() calls (editor palette drops) are NOT a populate cycle and never trigger this.
   */
  onAllSpawned(cb: () => void): void {
    if (this.allSpawned) {
      cb();
      return;
    }
    this.spawnCbs.push(cb);
  }

  /**
   * ── ⭐ "EVERY PLACEMENT HAS SPAWNED **AND** ITS CONTENT HAS ARRIVED" ────────────────────────────
   *
   * `onAllSpawned` is the two-phase INIT hook and it fires as soon as `loadAll` has constructed
   * every instance. That is the right moment to start cross-entity services and the WRONG moment
   * to measure the scene graph: `staticMesh.create()` returns synchronously and adds its GLB
   * inside a `.then()`, so on `sponza-debug-01` `onAllSpawned` fires while 50 MB of glTF is still
   * parsing. Anything that reads `drawnBounds()` there sees a near-empty box — which is exactly
   * how a good baked atlas is refused for `geometryDrift` on every boot, with a plausible,
   * self-consistent and completely wrong verdict.
   *
   * So this is the second signal: `onAllSpawned`, then `allSettled` over every instance's optional
   * `ready`. It is deliberately a HOST seam rather than a GI one — the baked-lighting load, the
   * editor's thumbnailer, a future "first complete frame" gate and any screenshot harness all want
   * the same fact, and building it into the one caller that needs it first is how a category
   * becomes a special case.
   *
   * `allSettled`, never `all`: an entity whose asset 404s has already drawn its placeholder and is
   * as ready as it will ever be. A rejection here must not strand the callback (hard rule 6).
   *
   * ⚠️ It fires ONCE per populate cycle, asynchronously — ALWAYS on a later microtask, even when
   * everything is already settled — so a caller can never be re-entered from inside `loadAll`.
   * Instances spawned after the cycle (an editor palette drop) are not waited on.
   */
  onAllLoaded(cb: () => void): void {
    this.onAllSpawned(() => {
      const pending = this.instances.map((i) => i.ready).filter((p): p is Promise<unknown> => !!p);
      void Promise.allSettled(pending).then(() => cb());
    });
  }

  /**
   * L3 · Subscribe to the `placing` counter. Returns the unsubscribe.
   *
   * Deliberately NOT another `onAllSpawned`-shaped once-per-cycle hook: this is the ONE fact the
   * first half of a boot can report honestly, and it is only worth anything WHILE the cycle runs.
   * It fires once when a cycle starts (with `placed: 0`, so a subscriber learns the denominator
   * before anything has landed) and once per placement after.
   */
  onPlaced(cb: (placed: number, total: number) => void): () => void {
    this.placedCbs.push(cb);
    return () => {
      const i = this.placedCbs.indexOf(cb);
      if (i >= 0) this.placedCbs.splice(i, 1);
    };
  }

  /** Placements processed in the current populate cycle. */
  placedCount(): number {
    return this.placedN;
  }

  private emitPlaced(total: number): void {
    // A subscriber throwing must not abort a populate cycle (rule 6): the level still has to load.
    for (const cb of [...this.placedCbs]) {
      try {
        cb(this.placedN, total);
      } catch (err) {
        this.warnOnce(`onPlaced subscriber threw: ${String(err)}`);
      }
    }
  }

  /** Drain-loop guards re-entry: a callback registering another lands in the live queue here. */
  private flushSpawnCbs(): void {
    while (this.spawnCbs.length > 0) {
      const batch = this.spawnCbs.splice(0);
      for (const cb of batch) cb();
    }
  }

  /**
   * Broadcast editor-mode visibility (ADR-0002): mount calls this on editor enter/exit; classes
   * with editor-only visuals implement setEditorVisible, everyone else is unaffected. The flag
   * sticks, so instances spawned mid-editor-session come up in the right state.
   */
  setAllEditorVisible(v: boolean): void {
    this.editorVisible = v;
    for (const inst of this.instances) inst.setEditorVisible?.(v);
  }

  /** Per-frame drive — mount calls this once per render frame after the tick loop. */
  update(player: PlayerSnapshot, dt: number): void {
    for (const inst of this.instances) inst.update(player, dt);
  }

  /**
   * Live instance count. Diagnostics only (the dev hook's leak net reads it through
   * `window.__fwg`): an entity count that climbs across identical encounters is a dispose that
   * never ran.
   */
  entityCount(): number {
    return this.instances.length;
  }

  /** Dispose + forget one instance (editor delete/respawn path). */
  remove(instance: EntityInstance): void {
    const i = this.instances.indexOf(instance);
    if (i < 0) return;
    this.instances.splice(i, 1);
    instance.dispose();
  }

  dispose(): void {
    for (const inst of this.instances) inst.dispose();
    this.instances.length = 0;
    // The world is empty again: a dispose starts the next populate cycle, and any not-yet-fired
    // callbacks belonged to entities that just died with it.
    this.allSpawned = false;
    this.spawnCbs.length = 0;
  }

  private warnOnce(msg: string): void {
    if (this.warned.has(msg)) return;
    this.warned.add(msg);
    console.warn(`[entities] ${msg}`);
  }
}
